import Paper from '@/components/Paper'
import SubHeaders from '@/components/Headers/SubHeaders'
import {
    DataTableCustomized,
    createOptionsFormatter,
    type DataTableAction,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import { CreateEditTechnicalTemplateModalProps } from '@/modals/TechnicalTemplateModals/CreateEdit'
import { paths } from '@/utils/path'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    deleteTechnicalTemplate,
    getAllTechnicalTemplates,
} from '../../services'
import { TechnicalTemplate } from '../../types'

const TechnicalTemplatesList: React.FC = () => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    const {
        data: templates = [],
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.TECHNICAL_TEMPLATES],
            queryFn: getAllTechnicalTemplates,
        },
        {
            showLoading: true,
            showError: true,
        }
    )

    const { mutateAsync: deleteTemplate } = useMutationWithHandler(
        {
            mutationFn: deleteTechnicalTemplate,
        },
        {
            successMessage: t('Template deleted successfully'),
            reactQueryKeys: [QUERY_KEYS.TECHNICAL_TEMPLATES],
        }
    )

    const handleCreate = () => {
        setModal<CreateEditTechnicalTemplateModalProps>(
            'TechnicalTemplateModals/CreateEdit/index',
            { template: null }
        )
    }

    const handleEdit = (template: TechnicalTemplate) => {
        setModal<CreateEditTechnicalTemplateModalProps>(
            'TechnicalTemplateModals/CreateEdit/index',
            { template }
        )
    }

    const handleDelete = (template: TechnicalTemplate) => {
        setConfirmation({
            title: t('delete-template'),
            description: t('confirm-delete-template-description', {
                template_name: template.template_name,
            }),
            onConfirm: () => deleteTemplate(template.technical_template_id),
        })
    }

    const columns = useMemo(
        (): DataTableColumn<TechnicalTemplate>[] => [
            {
                key: 'technical_template_id',
                title: 'ID',
                width: 70,
            },
            {
                key: 'template_name',
                title: t('template-name'),
                width: 250,
            },
            {
                key: 'product_group_name',
                title: t('product-group'),
                width: 180,
            },
            {
                key: 'template_description',
                title: t('description'),
                width: 300,
                valueGetter: (row) => row.template_description || '-',
            },
            {
                key: 'dns_enable',
                title: 'DNS',
                width: 80,
                cellClassName: 'text-center',
                valueGetter: (row) =>
                    row.dns_enable
                        ? String(row.dns_managements?.length || 0)
                        : '-',
            },
            {
                key: 'metadata' as any,
                title: t('metadata'),
                width: 100,
                cellClassName: 'text-center',
                valueGetter: (row) => row.metadata?.length || 0,
            },
            {
                key: 'documentations' as any,
                title: 'Docs',
                width: 80,
                cellClassName: 'text-center',
                valueGetter: (row) => row.documentations?.length || 0,
            },
            {
                key: 'is_active',
                title: t('active'),
                width: 100,
                cellClassName: 'text-center',
                formatter: createOptionsFormatter({
                    options: [
                        { label: 'Active', value: true, color: 'success' },
                        { label: 'Inactive', value: false, color: 'error' },
                    ],
                    renderMode: 'chip',
                }),
            },
        ],
        [t]
    )

    const actions = useMemo(
        (): DataTableAction<TechnicalTemplate>[] => [
            {
                key: 'edit',
                label: t('edit'),
                variant: 'info',
                onClick: (row) => handleEdit(row),
            },
            {
                key: 'delete',
                label: t('delete'),
                variant: 'danger',
                onClick: (row) => handleDelete(row),
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            <SubHeaders
                actions={[
                    {
                        icon: <AddIcons />,
                        label: t('create-template'),
                        variant: 'contained',
                        onClick: handleCreate,
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.settings,
                        title: t('settings'),
                    },
                    {
                        path: paths.serviceTemplates,
                        title: t('service-templates'),
                    },
                ]}
            />

            <Paper>
                <DataTableCustomized<TechnicalTemplate>
                    columns={columns}
                    rows={templates}
                    rowKey={(row) => row.technical_template_id}
                    isFetching={isFetching}
                    emptyMessage={t('no-templates-found')}
                    bordered
                    size="small"
                    resizable
                    actions={actions}
                />
            </Paper>
        </div>
    )
}

export default TechnicalTemplatesList
